/**
 * Gates-board presentation helpers (J6, Wave 3).
 *
 * Pure status → icon/severity mapping and ordering. Gate STATUSES are
 * server-computed (`AutonomyGatesService`) — these helpers only choose how a
 * given status looks; an unrecognized status renders as `unknown` (fail
 * closed, never a crash and never an implied "ok").
 */

import type { AutonomyGateRow, AutonomyGateStatus } from '../types/gates.types';

export interface AutonomyGateStatusMeta {
  /** Glyph rendered inside the status circle. */
  icon: string;
  /** Theme mapping hint — components resolve to palette tokens, never literals. */
  severity: 'success' | 'warning' | 'neutral';
  /** Short human label for the status. */
  label: string;
}

export const AUTONOMY_GATE_STATUS_META: Record<AutonomyGateStatus, AutonomyGateStatusMeta> = {
  ok: { icon: '✓', severity: 'success', label: 'OK' },
  attention: { icon: '!', severity: 'warning', label: 'Attention' },
  not_built: { icon: '—', severity: 'neutral', label: 'Not built' },
  unknown: { icon: '…', severity: 'neutral', label: 'Check unavailable' },
};

/** Meta for a status; anything unrecognized falls back to `unknown` (fail closed). */
export function gateStatusMeta(status: AutonomyGateStatus | string): AutonomyGateStatusMeta {
  return (
    AUTONOMY_GATE_STATUS_META[status as AutonomyGateStatus] ?? AUTONOMY_GATE_STATUS_META.unknown
  );
}

/** Numeric part of a gate id ("G10" → 10); NaN-safe fallback pushes junk last. */
export function gateNumber(gateId: string): number {
  const parsed = Number.parseInt(gateId.replace(/^G/i, ''), 10);
  return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
}

/** Stable G1…G10 ordering regardless of server order; keeps every row. */
export function orderGates(gates: AutonomyGateRow[]): AutonomyGateRow[] {
  return [...gates].sort((a, b) => gateNumber(a.gateId) - gateNumber(b.gateId));
}

/** "Not built — owner: J5." board line for a gate with an owner. */
export function formatGateOwnerDetail(detail: string, owner?: string): string {
  if (!owner) return detail;
  return detail.includes(owner) ? detail : `${detail} — owner: ${owner}`;
}
